import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { Comment, CommentReply } from '../types';

export function getCommentActionErrorMessage(error: unknown, fallback: string): string {
  const code = typeof error === 'object'
    && error !== null
    && 'code' in error
    && typeof (error as { code?: unknown }).code === 'string'
      ? (error as { code: string }).code
      : '';

  if (code === 'permission-denied' || code === 'firestore/permission-denied') {
    return 'You do not have permission to comment on this project right now. Refresh the page and try again.';
  }

  return fallback;
}

export function subscribeToComments(
  projectId: string,
  fileId: string,
  onChange: (comments: Comment[]) => void
): () => void {
  const q = query(
    collection(db, 'projects', projectId, 'comments'),
    where('fileId', '==', fileId),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const comments = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as Omit<Comment, 'id' | 'createdAt'>),
      createdAt: doc.data().createdAt?.toDate?.() ?? new Date(),
      resolvedAt: doc.data().resolvedAt?.toDate?.() ?? null,
    })) as Comment[];
    onChange(comments);
  });
}

export async function addComment(
  projectId: string,
  comment: Omit<Comment, 'id' | 'createdAt'>
): Promise<string> {
  const docRef = await addDoc(
    collection(db, 'projects', projectId, 'comments'),
    {
      ...comment,
      createdAt: Timestamp.now(),
    }
  );
  return docRef.id;
}

export async function getReplies(
  projectId: string,
  commentId: string
): Promise<CommentReply[]> {
  const q = query(
    collection(db, 'projects', projectId, 'comments', commentId, 'replies'),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...(doc.data() as Omit<CommentReply, 'id' | 'createdAt'>),
    createdAt: doc.data().createdAt?.toDate?.() ?? new Date(),
  })) as CommentReply[];
}

export async function addReply(
  projectId: string,
  commentId: string,
  reply: Omit<CommentReply, 'id' | 'createdAt'>
): Promise<string> {
  const docRef = await addDoc(
    collection(db, 'projects', projectId, 'comments', commentId, 'replies'),
    {
      ...reply,
      createdAt: Timestamp.now(),
    }
  );
  return docRef.id;
}

export async function resolveComment(
  projectId: string,
  commentId: string,
  resolvedBy: string
): Promise<void> {
  await updateDoc(doc(db, 'projects', projectId, 'comments', commentId), {
    resolved: true,
    resolvedAt: Timestamp.now(),
    resolvedBy,
  });
}
